import moment from 'moment'
import { dueDateAsDay } from 'app/lib/taskDueDate'
import type { DayRange, TaskCalendarView } from 'app/types/task-calendar'

/** Due dates are whole calendar days (see `taskDueDate.ts`), so every view buckets by day. */

export const TASK_CALENDAR_VIEWS: Readonly<TaskCalendarView[]> = [`day`, `week`, `month`]

export const DAY_KEY = `YYYY-MM-DD`

/** Inclusive. Month covers the whole grid, neighbouring months' days included. */
export function visibleRange(view: TaskCalendarView, anchor: moment.MomentInput): DayRange {
  const day = moment(anchor).startOf(`day`)
  if (view === `day`) return { start: day, end: day.clone() }
  if (view === `week`) return { start: day.clone().startOf(`week`), end: day.clone().endOf(`week`).startOf(`day`) }
  return {
    start: day.clone().startOf(`month`).startOf(`week`),
    end: day.clone().endOf(`month`).endOf(`week`).startOf(`day`),
  }
}

/**
 * The page's task search narrowed to a range of due days, or to undated tasks.
 * Due dates are stored as midnight UTC of the intended day, so the range
 * compares UTC calendar days with an exclusive bound on the day after `end`.
 * The page query is parenthesized so a top-level OR in it (My Tasks' owner or
 * role) stays scoped.
 */
export function dueDateSearch(query: string, range: DayRange | `undated`): string {
  const clause =
    range === `undated`
      ? `!dueDate:*`
      : `dueDate:[${range.start.format(DAY_KEY)} TO ${range.end.clone().add(1, `day`).format(DAY_KEY)}}`
  return query.trim() ? `(${query}) AND ${clause}` : clause
}

/** Tasks keyed by their due day (`YYYY-MM-DD`). Undated tasks are left out. */
export function tasksByDay<T extends { dueDate?: string | null }>(tasks: T[]): Map<string, T[]> {
  const byDay = new Map<string, T[]>()
  for (const task of tasks) {
    if (!task.dueDate) continue
    const key = dueDateAsDay(task.dueDate).format(DAY_KEY)
    const bucket = byDay.get(key)
    if (bucket) bucket.push(task)
    else byDay.set(key, [task])
  }
  return byDay
}

/** Toolbar title: "October 2026", "Oct 11 to 17, 2026", or "Wednesday, October 14". */
export function rangeTitle(view: TaskCalendarView, anchor: moment.MomentInput): string {
  const day = moment(anchor)
  if (view === `month`) return day.format(`MMMM YYYY`)
  if (view === `day`) return day.format(`dddd, MMMM D`)
  const { start, end } = visibleRange(`week`, day)
  if (start.year() !== end.year()) return `${start.format(`MMM D, YYYY`)} to ${end.format(`MMM D, YYYY`)}`
  if (start.month() !== end.month()) return `${start.format(`MMM D`)} to ${end.format(`MMM D, YYYY`)}`
  return `${start.format(`MMM D`)} to ${end.format(`D, YYYY`)}`
}

/** `moment().calendar()` formats for a day as it reads after "due": "today", "on Friday", "last Monday". */
export const DUE_DAY_PHRASES: Readonly<moment.CalendarSpec> = {
  sameDay: `[today]`,
  nextDay: `[tomorrow]`,
  lastDay: `[yesterday]`,
  nextWeek: `[on] dddd`,
  lastWeek: `[last] dddd`,
  sameElse: `[on] MMMM D`,
}

/** dataTransfer type the week board's columns accept. */
export const TASK_DRAG_TYPE = `application/x-trackable-task-id`
